package domain.cards

import domain.levels.RoomObjective

data class ActiveCard(
    val definition: CardDefinition,
    var roundsRemaining: Int?,
)

open class RunState {
    var coins: Int = 0
        private set

    var level: Int = 1

    private val _activeCards = mutableListOf<ActiveCard>()
    val activeCards: List<ActiveCard> get() = _activeCards

    fun advanceRound() {
        _activeCards.removeAll { card ->
            card.roundsRemaining?.let {
                card.roundsRemaining = it - 1
                it - 1 <= 0
            } ?: false
        }
    }

    fun activateCard(card: CardDefinition) {
        _activeCards.add(
            ActiveCard(
                definition      = card,
                roundsRemaining = card.durationRounds,
            )
        )
    }

    fun disableCard(card: CardDefinition) {
        _activeCards.removeAll { it.definition.id == card.id }
    }

    /**
     * Disable all cards whose effects include [CardEffect.SetCustomPieceSet].
     * Called when a [CardEffect.RestoreTetrominoes] card is activated so that
     * pentomino/tromino cards are cleanly removed from the active set.
     */
    fun disableAllPieceSetCards() {
        _activeCards.removeAll { ac ->
            ac.definition.effects.any { it is CardEffect.SetCustomPieceSet }
        }
    }

    fun addCoins(amount: Int) {
        coins += amount
    }

    fun spendCoins(amount: Int): Boolean {
        if (coins < amount) return false
        coins -= amount
        return true
    }

    // ── Score / coins ─────────────────────────────────────────────────────────

    val totalScoreMultiplier: Double
        get() = _activeCards.flatMap { it.definition.effects }
            .filterIsInstance<CardEffect.SetScoreMultiplier>()
            .fold(1.0) { acc, e -> acc * e.multiplier }

    val totalCoinMultiplier: Double
        get() = _activeCards.flatMap { it.definition.effects }
            .filterIsInstance<CardEffect.SetCoinMultiplier>()
            .fold(1.0) { acc, e -> acc * e.multiplier }

    // ── Piece set ─────────────────────────────────────────────────────────────

    /** Returns the most-recently activated piece-set card's effect, or null if tetrominoes are active. */
    val activePieceSet: CardEffect.SetCustomPieceSet?
        get() = _activeCards.lastOrNull { card ->
            card.definition.effects.any { it is CardEffect.SetCustomPieceSet }
        }?.definition?.effects?.filterIsInstance<CardEffect.SetCustomPieceSet>()?.firstOrNull()

    /** True when a RestoreTetrominoes card is currently active (blocks piece-set offers). */
    val isRestoredToTetrominoes: Boolean
        get() = _activeCards.any { ac ->
            ac.definition.effects.any { it is CardEffect.RestoreTetrominoes }
        }

    // ── Gravity ───────────────────────────────────────────────────────────────

    /** All gravity multipliers compound — multiple cards stack. */
    val gravityMultiplier: Double
        get() = _activeCards.flatMap { it.definition.effects }
            .filterIsInstance<CardEffect.SetGravityMultiplier>()
            .fold(1.0) { acc, e -> acc * e.multiplier }

    /** Effective lock-delay override in ms. Null means use engine default. */
    val lockDelayOverrideMs: Double?
        get() = _activeCards.flatMap { it.definition.effects }
            .filterIsInstance<CardEffect.SetLockDelay>()
            .lastOrNull()?.ms

    /**
     * Effective preview size override. Null means use engine default (5).
     * The minimum across all active [CardEffect.SetPreviewSize] cards wins
     * so restrictive cards always take precedence.
     */
    val activePreviewSize: Int?
        get() = _activeCards.flatMap { it.definition.effects }
            .filterIsInstance<CardEffect.SetPreviewSize>()
            .minOfOrNull { it.count }

    /** Whether hold is currently disabled by a card. */
    val isHoldDisabled: Boolean
        get() = _activeCards.flatMap { it.definition.effects }
            .filterIsInstance<CardEffect.SetHoldEnabled>()
            .any { !it.enabled }

    // ── Room objective ────────────────────────────────────────────────────────

    /**
     * The active room objective, resolved in priority order:
     * 1. If [FreezeBoardMode] is active, it forces a Lines objective.
     * 2. Otherwise the last [SetRoomObjective] card wins.
     * 3. Default: [RoomObjective.Lines] with target 10.
     */
    val activeObjective: RoomObjective
        get() {
            val freeze = _activeCards.flatMap { it.definition.effects }
                .filterIsInstance<CardEffect.FreezeBoardMode>()
                .firstOrNull()
            if (freeze != null) return RoomObjective.Lines(freeze.nextLevelLines.toDouble())

            return _activeCards.flatMap { it.definition.effects }
                .filterIsInstance<CardEffect.SetRoomObjective>()
                .lastOrNull()?.objective
                ?: RoomObjective.Lines(10.0)
        }

    // ── Board flags ───────────────────────────────────────────────────────────

    val isFreezeBoard: Boolean
        get() = _activeCards.flatMap { it.definition.effects }
            .any { it is CardEffect.FreezeBoardMode }

    val freezeNextLevelLines: Int
        get() = _activeCards.flatMap { it.definition.effects }
            .filterIsInstance<CardEffect.FreezeBoardMode>()
            .firstOrNull()?.nextLevelLines ?: 40

    /** Initial blocked garbage to spawn at round start (last card wins). */
    val spawnBlockedGarbage: CardEffect.SpawnBlockedGarbage?
        get() = _activeCards.flatMap { it.definition.effects }
            .filterIsInstance<CardEffect.SpawnBlockedGarbage>()
            .lastOrNull()

    // ── Economy / game-over check ─────────────────────────────────────────────

    /**
     * Returns true when the player cannot afford any card in [options].
     * When this is true after a room completes the run ends — there is no
     * path forward.
     */
    fun canAffordAny(options: List<CardDefinition>): Boolean =
        options.any { coins >= it.coinCost }

    open fun reset() {
        _activeCards.clear()
        coins = 0
        level = 1
    }
}
